import * as Cesium from "cesium";
import { getUsernameAndUserIdFormat } from "../Utility";

let trafficSignModeEnabled = false;
let trafficSignHandler = null;
let trafficSignEscHandler = null;

function getNumericPropertyId(entity) {
  try {
    const prop = entity?.properties?.id;
    const value = prop && typeof prop.getValue === "function" ? prop.getValue(Cesium.JulianDate.now()) : prop;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function collectIdsFromGeoJSON(gjson, out = new Set()) {
  if (!gjson?.features || !Array.isArray(gjson.features)) return out;

  for (const f of gjson.features) {
    const n = Number(f?.properties?.id);
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
}

function collectIdsFromDataSource(dataSource, out = new Set()) {
  const entities = dataSource?.entities?.values || [];

  for (const entity of entities) {
    const n = getNumericPropertyId(entity);
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
}

function normalizeLayerIdentity(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[\s\-_]/g, "");
}

function findMapObjectByDataSource(dataSource, mapObjectsRef) {
  const list = Array.isArray(mapObjectsRef?.current) ? mapObjectsRef.current : [];

  return (
    list.find((o) => {
      if (!o || !dataSource) return false;
      if (o.dataSource === dataSource || o.laneletDataSource === dataSource) return true;
      if (dataSource.layerId != null && o.layer_id != null && String(o.layer_id) === String(dataSource.layerId)) return true;

      const a = normalizeLayerIdentity(o.file_name || o.layer_name || o.name);
      const b = normalizeLayerIdentity(dataSource.layerName || dataSource.name);
      return a && b && a === b;
    }) || null
  );
}

function getNextIdFromDataSource(dataSource, mapObjectsRef = null, setMapObjects = null) {
  const layer = findMapObjectByDataSource(dataSource, mapObjectsRef);
  const ids = new Set();

  for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson]) {
    collectIdsFromGeoJSON(gjson, ids);
  }

  collectIdsFromDataSource(dataSource, ids);

  const stored = Number(layer?.bboxMaxFeaturePropertiesId ?? layer?.bboxMaxId);
  const lastId = Number(layer?.geojson_last_id);

  let maxId = -2;
  if (Number.isFinite(stored)) maxId = Math.max(maxId, stored);
  if (Number.isFinite(lastId)) maxId = Math.max(maxId, lastId);

  for (const id of ids) {
    if (id > maxId) maxId = id;
  }

  let nextId = maxId + 2;
  while (ids.has(nextId)) {
    nextId += 2;
  }

  if (layer) {
    layer.geojson_last_id = nextId;
    layer.bboxMaxId = nextId;
    layer.bboxMaxFeaturePropertiesId = nextId;
  }

  const patch = (o) => {
    if (!o || !dataSource) return o;

    const sameLayer =
      o === layer ||
      o.dataSource === dataSource ||
      o.laneletDataSource === dataSource ||
      (dataSource.layerId != null && o.layer_id != null && String(o.layer_id) === String(dataSource.layerId)) ||
      (normalizeLayerIdentity(o.file_name || o.layer_name || o.name) && normalizeLayerIdentity(o.file_name || o.layer_name || o.name) === normalizeLayerIdentity(dataSource.layerName || dataSource.name));

    if (!sameLayer) return o;

    return {
      ...o,
      geojson_last_id: nextId,
      bboxMaxId: nextId,
      bboxMaxFeaturePropertiesId: nextId,
    };
  };

  if (Array.isArray(mapObjectsRef?.current)) {
    mapObjectsRef.current = mapObjectsRef.current.map(patch);
  }

  if (typeof setMapObjects === "function") {
    setMapObjects((prev) => (Array.isArray(prev) ? prev.map(patch) : prev));
  }

  return nextId;
}

export function toggleTrafficSignTool(viewer, onCreateFeatures, destroy = false, signBoxDataSource = null, signPoleDataSource = null, userData, mapObjectsRef = null, setMapObjects = null) {
  if (!viewer) return false;

  if (destroy === true) {
    trafficSignModeEnabled = false;
    if (trafficSignHandler) {
      trafficSignHandler.destroy();
      trafficSignHandler = null;
    }
    if (trafficSignEscHandler) {
      window.removeEventListener("keydown", trafficSignEscHandler, { capture: true });
      trafficSignEscHandler = null;
    }

    return false;
  }

  if (trafficSignModeEnabled && trafficSignHandler) {
    if (!trafficSignEscHandler) {
      trafficSignEscHandler = (e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();

        trafficSignModeEnabled = false;

        if (trafficSignHandler) {
          trafficSignHandler.destroy();
          trafficSignHandler = null;
        }

        if (trafficSignEscHandler) {
          window.removeEventListener("keydown", trafficSignEscHandler, { capture: true });
          trafficSignEscHandler = null;
        }
      };

      window.addEventListener("keydown", trafficSignEscHandler, { capture: true });
    }
    return true;
  }

   trafficSignModeEnabled = true;
  trafficSignHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
  const creator = getUsernameAndUserIdFormat(userData);

  trafficSignEscHandler = (e) => {
    if (e.key !== "Escape") return;
    e.preventDefault();

    trafficSignModeEnabled = false;

    if (trafficSignHandler) {
      trafficSignHandler.destroy();
      trafficSignHandler = null;
    }

    if (trafficSignEscHandler) {
      window.removeEventListener("keydown", trafficSignEscHandler, { capture: true });
      trafficSignEscHandler = null;
    }
  };

  window.addEventListener("keydown", trafficSignEscHandler, { capture: true });

  trafficSignHandler.setInputAction(function (click) {
    if (!trafficSignModeEnabled) return;

    const scene = viewer.scene;
    const ellipsoid = Cesium.Ellipsoid.WGS84;

    let cartesian = scene.pickPosition(click.position);
    if (!cartesian) {
      cartesian = viewer.camera.pickEllipsoid(click.position, ellipsoid);
    }
    if (!cartesian) return;

    const baseCarto = ellipsoid.cartesianToCartographic(cartesian);
    if (!baseCarto) return;

    const baseLon = Cesium.Math.toDegrees(baseCarto.longitude);
    const baseLat = Cesium.Math.toDegrees(baseCarto.latitude);
    const baseH = baseCarto.height || 0.0;

    // Local ENU frame
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(cartesian);
    const east = Cesium.Matrix4.getColumn(enu, 0, new Cesium.Cartesian3());
    const north = Cesium.Matrix4.getColumn(enu, 1, new Cesium.Cartesian3());
    Cesium.Cartesian3.normalize(east, east);
    Cesium.Cartesian3.normalize(north, north);

    function offsetFrom(origin, eastMeters, northMeters) {
      const eOff = Cesium.Cartesian3.multiplyByScalar(east, eastMeters, new Cesium.Cartesian3());
      const nOff = Cesium.Cartesian3.multiplyByScalar(north, northMeters, new Cesium.Cartesian3());
      const tmp = Cesium.Cartesian3.add(origin, eOff, new Cesium.Cartesian3());
      return Cesium.Cartesian3.add(tmp, nOff, new Cesium.Cartesian3());
    }

    function toLonLat(pos) {
      const c = ellipsoid.cartesianToCartographic(pos);
      return [Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude)];
    }

    // Requirements
    const halfWidth = 0.5;
    const boxHeight = 0.5;

    const leftPos = offsetFrom(cartesian, -halfWidth, 0.0);
    const rightPos = offsetFrom(cartesian, +halfWidth, 0.0);

    const L = toLonLat(leftPos);
    const R = toLonLat(rightPos);

    let boxFeature = null;
    let poleFeature = null;

    // NEW: keep entity refs to pass out
    let boxEntity = null;
    let poleEntity = null;

    // --------------------------
    // SignPole (style from FIRST entity)
    // --------------------------
    let poleId = null;
    if (signPoleDataSource) {
      const entities = signPoleDataSource.entities;
      const all = entities.values;
      const first = all.length > 0 ? all[0] : null;

           poleId = getNextIdFromDataSource(signPoleDataSource, mapObjectsRef, setMapObjects);

      let pointOptions = {
        pixelSize: 10,
        color: Cesium.Color.RED,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      };

      if (first && first.point) {
        const p = first.point;
        pointOptions = {
          pixelSize: p.pixelSize,
          color: p.color,
          outlineColor: p.outlineColor,
          outlineWidth: p.outlineWidth,
          heightReference: p.heightReference,
          disableDepthTestDistance: p.disableDepthTestDistance,
        };
      }

          poleEntity = entities.add({
        position: Cesium.Cartesian3.fromDegrees(baseLon, baseLat, baseH),
        point: pointOptions,
        properties: new Cesium.PropertyBag({
          id: new Cesium.ConstantProperty(poleId),
          ...(creator && {
            creator: new Cesium.ConstantProperty(creator),
          }),
        }),
      });

      poleFeature = {
        type: "Feature",
        geometry: { type: "Point", coordinates: [baseLon, baseLat] },
        properties: {
          kind: "traffic_sign_pole",
          entityId: poleEntity.id,
          id: poleId,
        },
      };
    }

    // --------------------------
    // SignBox -> ADD POLYGON ENTITY to signBoxDataSource
    // --------------------------
    let boxId = null;
    if (signBoxDataSource) {
      const entities = signBoxDataSource.entities;
      const all = entities.values;
      const first = all.length > 0 ? all[0] : null;

          boxId = getNextIdFromDataSource(signBoxDataSource, mapObjectsRef, setMapObjects);

      // Polygon positions (with heights)
      const hierarchyPositions = Cesium.Cartesian3.fromDegreesArrayHeights([L[0], L[1], baseH, R[0], R[1], baseH, R[0], R[1], baseH + boxHeight, L[0], L[1], baseH + boxHeight, L[0], L[1], baseH]);

      let polygonOptions = {
        hierarchy: hierarchyPositions,
        material: Cesium.Color.YELLOW.withAlpha(0.15),
        outline: true,
        outlineColor: Cesium.Color.YELLOW,
        perPositionHeight: true,
      };

      // Copy polygon style from first entity (if any)
      if (first && first.polygon) {
        const pg = first.polygon;
        polygonOptions = {
          hierarchy: hierarchyPositions,
          material: pg.material,
          outline: pg.outline,
          outlineColor: pg.outlineColor,
          perPositionHeight: true,
        };
      }

           boxEntity = entities.add({
        polygon: polygonOptions,
        properties: new Cesium.PropertyBag({
          id: new Cesium.ConstantProperty(boxId),
          ...(creator && {
            creator: new Cesium.ConstantProperty(creator),
          }),
          ...(poleId !== null && {
            traffic_sign_pole_id: new Cesium.ConstantProperty(poleId),
          }),
        }),
      });

      const boxProps = {
        kind: "traffic_sign_box",
        entityId: boxEntity.id,
        id: boxId,
      };
      if (poleId !== null) boxProps.traffic_sign_pole_id = poleId;

      // GeoJSON polygon feature (same as polygon entity)
      const p1 = [L[0], L[1], baseH];
      const p2 = [R[0], R[1], baseH];
      const p3 = [R[0], R[1], baseH + boxHeight];
      const p4 = [L[0], L[1], baseH + boxHeight];
      const p5 = [L[0], L[1], baseH];

      boxFeature = {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [[p1, p2, p3, p4, p5]],
        },
        properties: boxProps,
      };
    }

    if (!boxFeature && !poleFeature) return;

    // NEW: pass entities too (3rd and 4th params)
    onCreateFeatures(boxFeature, poleFeature, boxEntity, poleEntity);
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  return true;
}
